import React, { useEffect, useMemo, useState } from "react";
import { useIsReadOnlyManager } from "@/shared/readOnlyManager";
import { usePagination, useTable, useRowSelect } from "react-table";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Table,
  Container,
  Row,
  Col,
  Label,
  Input,
} from "reactstrap";
import DatePicker from "react-datepicker";
import {
  flashError,
  flashSuccess,
  useDispatch,
} from "@/shared/components/flash";
import axios from "axios";
import ReactTablePagination from "@/shared/components/table/components/ReactTablePagination";
import { TABLE_COLUMNS } from "./Column";
import { connect } from "react-redux";
import apiTableService from "@/services/apiTableService";
import IndeterminateCheckbox from "@/shared/components/form/IndeterminateCheckbox";
import { dateStrToYmd } from "@/shared/helpers";
import AwardApprovalPopup from "./AwardApprovalPopup";
import { hasUserPermissions } from "@/services/program/budget";
import { TableSkeleton } from "@/shared/components/Skeletons";

const ACTIONS = [
  { label: "Approved", name: "approved" },
  { label: "Rejected", name: "reject" },
];

const AWARD_APPROVE = "Award Approve";
const AWARD_APPROVE_ALL = "Award Approve All";
const CAN_ACCESS_AWARD_PERMISSION = "can_access_award_permission";

const queryPageSize = 10;
const SEARCH_DEBOUNCE_MS = 300;

const PendingAwardApprovalsTable = ({
  auth,
  organization,
  program,
  assignedPermissions,
}) => {
  const [participants, setParticipants] = useState(null);
  const [loading, setLoading] = useState(true);
  const [awardApprovalParticipants, setAwardApprovalParticipants] = useState(
    []
  );
  const [statusName, setStatusName] = useState("");
  const [isOpen, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [startDate, setStartDate] = useState(new Date(new Date().setFullYear(new Date().getFullYear() - 2)));
  const [endDate, setEndDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Debounce the search box so typing fires one refetch, not one per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const dispatch = useDispatch();

  const toggle = () => {
    setOpen((prevState) => !prevState);
  };

  // "Award Approve All" marks its holder a super-user who bypasses the
  // per-program approval flow (see EntrataApprovalGateService) — it should
  // grant at least what plain "Award Approve" grants here too, same as the
  // ICOV3-1666 fix in ProgramParticipants.jsx.
  const readOnly = useIsReadOnlyManager();
  const canApproveAwards = !readOnly && (
    hasUserPermissions(assignedPermissions, AWARD_APPROVE, CAN_ACCESS_AWARD_PERMISSION) ||
    hasUserPermissions(assignedPermissions, AWARD_APPROVE_ALL, CAN_ACCESS_AWARD_PERMISSION)
  );

  const SELECTION_COLUMN = {
    id: "selection",
    Header: ({ getToggleAllPageRowsSelectedProps }) => (
      <div>
        <IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} />
      </div>
    ),
    Cell: ({ row }) => (
      <div>
        <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
      </div>
    ),
  };

  let final_columns = React.useMemo(() => {
    let column = [];
    if (canApproveAwards) {
      column = [SELECTION_COLUMN, ...TABLE_COLUMNS];
    } else {
      column = TABLE_COLUMNS;
    }
    // console.log(column);
    // console.log(program.rootProgram.use_cascading_approvals);
    // //program.rootProgram.use_budget_cascading=0;
    const filteredColumns = column?.filter((column) => {
      if (column.accessor === "scheduled_date") {
        return true;
      }
     if (column.accessor === "budgets_available") { 
      // Keep budgets_available only if use_budget_cascading is enabled 
       return program?.rootProgram?.use_budget_cascading === 1; 
      }
       if (column.accessor === "approved_by") { 
      // Keep approved_by only if use_cascading_approvals is enabled 
       return program?.rootProgram?.use_cascading_approvals === 1; 
      }
      if (column.accessor === "approval_status") { 
        // Always include approval_status column 
         return false;
         } 
      if (column.accessor === "rejection_notes") {
        // Always include rejection_notes column 
         return false;
       }
      
      return true;
    });
    return filteredColumns;
  }, [assignedPermissions]);

  const onSelectAction = (name) => {
    const rows = selectedFlatRows.map((d) => d.original);
    if (rows.length === 0) {
      alert("Select participants");
      return;
    }
    setAwardApprovalParticipants(rows);
    setStatusName(name);
    toggle();
  };

  const tableInstance = useTable(
    {
      columns: final_columns,
      data: useMemo(
        () => (participants ? participants?.results : []),
        [participants]
      ),
      initialState: {
        pageIndex: 0,
        pageSize: queryPageSize,
      },
      manualPagination: true,
      pageCount: Math.ceil(participants?.count / queryPageSize),
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetPage: false,
    },
    usePagination,
    useRowSelect
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    pageCount,
    pageOptions,
    gotoPage,
    previousPage,
    canPreviousPage,
    nextPage,
    canNextPage,
    setPageSize,
    selectedFlatRows,
    allColumns,
    getToggleHideAllColumnsProps,
    getToggleHiddenProps,
    state: { pageIndex, pageSize },
  } = tableInstance;

  const fetchData = () => {
    setLoading(true);
    if (organization?.id && program?.id) {
      // A referral-submission pending award is created regardless of use_cascading_approvals
      // (ReferralService never reads that flag) — it's the only thing that would ever be
      // pending on a program with cascading approvals off, since every other award type
      // auto-issues immediately in that state. Scope this table to referral-only in that
      // case so it never surfaces anything a manager on a non-cascading program could not
      // have been shown before (ICOV3-1780).
      const params = {
        start_date: dateStrToYmd(startDate),
        ...(endDate && { end_date: dateStrToYmd(endDate) }),
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(!program?.rootProgram?.use_cascading_approvals && { category: "referral" }),
      };
      apiTableService
        .fetchData({
          url: `/organization/${organization.id}/program/${program.id}/manager-pending-approvals`,
          page: pageIndex,
          size: queryPageSize,
          filter: params,
        })
        .then((items) => {
          if (items) {
            setParticipants(items);
            setLoading(false);
          }
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    fetchData();
  }, [organization, program, pageIndex, queryPageSize, startDate, endDate, debouncedSearchTerm]);

  const ActionsDropdown = () => {
    return (
      <div className="d-flex gap-2">
        <button className="btn btn-sm btn-primary" style={{ borderRadius: 4 }} onClick={() => onSelectAction('approved')}>Approve</button>
        <button className="btn btn-sm btn-secondary" style={{ borderRadius: 4 }} onClick={() => onSelectAction('reject')}>Reject</button>
      </div>
    );
  };

  const onSubmit = (values) => {
    try {
      if (awardApprovalParticipants.length > 0) {
        let formData = {};
        setSaving(true);
        formData.budget_cascading_approval_id = awardApprovalParticipants?.map(
          (approvalParticipant) => approvalParticipant.id
        );
        formData.approved = statusName == "approved" ? 1 : 2;
        formData.manager_id = 0;
        formData.rejection_note =
          statusName == "approved"
            ? `Approved by ${auth.name}`
            : values.rejection_note;
        formData.entrata_user_invite_id = awardApprovalParticipants?.[0]?.entrata_user_invite_id ?? null;

        axios
          .put(
            `/organization/${organization?.id}/program/${program?.id}/budget-cascading-approval`,
            formData
          )
          .then((response) => {
            if (response.status === 200) {
              flashSuccess(
                dispatch,
                "Award Approval status updated successfully!"
              );
              setSaving(false);
              toggle();
              fetchData();
            }
          })
          .catch((error) => {
            flashError(dispatch, error.message);
            setSaving(false);
          });
      }
    } catch (error) {
      flashError(dispatch, error.message);
    }
  };

  const manualPageSize = [];
  return (
      <div>
        <div className="users">
          <div className="mb-2" style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, whiteSpace: 'nowrap', color: '#666' }}>From Date</span>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                dateFormat="yyyy-MM-dd"
                className="form-control form-control-sm"
                style={{ width: 130 }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, whiteSpace: 'nowrap', color: '#666' }}>To Date</span>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                dateFormat="yyyy-MM-dd"
                className="form-control form-control-sm"
                isClearable
                placeholderText="Select end date"
                style={{ width: 130 }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, whiteSpace: 'nowrap', color: '#666' }}>Search</span>
              <Input
                type="text"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control form-control-sm"
                style={{ width: 150 }}
              />
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              <UncontrolledDropdown>
                <DropdownToggle caret className="btn btn-sm btn-outline-secondary" style={{ borderRadius: 4 }}>
                  Toggle Columns
                </DropdownToggle>
                <DropdownMenu>
                  {allColumns &&
                    allColumns.map((column) => (
                      <DropdownItem key={column.id} toggle={false}>
                        <input
                          type="checkbox"
                          {...column.getToggleHiddenProps()}
                          style={{ marginRight: "8px" }}
                        />
                        <label>{typeof column.Header === 'string' ? column.Header : ''}</label>
                      </DropdownItem>
                    ))}
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          </div>
          {canApproveAwards && (
            <div className="d-flex gap-2" style={{ paddingLeft: 15, marginTop: 0 }}>
              <ActionsDropdown />
            </div>
          )}
          <div className="points-summary-table">
            {loading ? (
              <TableSkeleton variant="rect" rows={3} columns={7} width={"100%"} height={20} />
            ) : (
            <Table striped borderless size="md" className="table--stack-mobile" {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps()}>
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {rows.map((row, i) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        return (
                          <td
                            {...cell.getCellProps()}
                            data-label={typeof cell.column.Header === "string" ? cell.column.Header : ""}
                          >
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={100} className="text-center py-3">No data to display</td></tr>
                )}
              </tbody>
            </Table>
            )}
          </div>
        </div>

        <div>
          {!loading && rows.length > 0 && (
            <>
              <ReactTablePagination
                page={page}
                gotoPage={gotoPage}
                previousPage={previousPage}
                nextPage={nextPage}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageOptions={pageOptions}
                pageSize={pageSize}
                pageIndex={pageIndex}
                pageCount={pageCount}
                setPageSize={setPageSize}
                manualPageSize={manualPageSize}
                dataLength={participants ? participants.count : 0}
              />
            </>
          )}
        </div>
        {awardApprovalParticipants && (
          <AwardApprovalPopup
            isOpen={isOpen}
            setOpen={setOpen}
            toggle={toggle}
            auth={auth}
            organization={organization}
            program={program}
            statusName={statusName}
            awardApprovalParticipants={awardApprovalParticipants}
            rejection_notes={`User Rejected by ${auth?.name}`}
            onSubmit={onSubmit}
            loading={saving}
          />
        )}
      </div>
    );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    program: state.program,
    organization: state.organization,
    rootProgram: state.rootProgram,
  };
};
export { PendingAwardApprovalsTable };
export default connect(mapStateToProps)(PendingAwardApprovalsTable);
